import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAiUsage, AiUsageResponse } from '../services/api';

/**
 * Per-tenant AI spend report for super-admin (2026-10-23).
 *
 * Pulls:
 *   analyzer      → sum of skill_outputs.cost_micros / prompt_tokens / completion_tokens
 *   transcription → sum of sim_calls.transcription_cost_micros / audio_duration_sec
 *
 * Date range defaults to the trailing 30 days; user can override.
 */

const card: React.CSSProperties = {
  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10,
  padding: 20, marginBottom: 16,
};
const th: React.CSSProperties = {
  textAlign: 'left', padding: '10px 12px', fontSize: 12,
  color: '#6b7280', fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.05em', borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap',
};
const thRight: React.CSSProperties = { ...th, textAlign: 'right' };
const td: React.CSSProperties = {
  padding: '12px', borderBottom: '1px solid var(--border)', fontSize: 14,
};
const tdRight: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

const usd = (n: number) =>
  '$' + Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const num = (n: number) => Number(n ?? 0).toLocaleString('en-US');

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

const AiUsagePage: React.FC = () => {
  const today     = useMemo(() => new Date(), []);
  const monthAgo  = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d; }, []);

  const [from, setFrom] = useState<string>(isoDate(monthAgo));
  const [to,   setTo]   = useState<string>(isoDate(today));
  const [data, setData] = useState<AiUsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      setData(await fetchAiUsage({ from, to }));
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const presets: Array<{ label: string; days: number }> = [
    { label: '7 days',  days: 7  },
    { label: '30 days', days: 30 },
    { label: '90 days', days: 90 },
  ];
  const setPreset = (days: number) => {
    const t = new Date();
    const f = new Date(); f.setDate(f.getDate() - days);
    setFrom(isoDate(f));
    setTo(isoDate(t));
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 8 }}>AI Usage &amp; Spend</h1>
      <p style={{ color: '#6b7280', marginBottom: 24 }}>
        Per-tenant breakdown of Deepgram transcription + OpenAI/DeepSeek
        analyzer spend. Costs are estimated using the public per-model
        rates in <code>ModelPricing.php</code> — treat as ballpark, not
        billing-grade. Only priced rows are counted.
      </p>

      <div style={card}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <label htmlFor="ai-usage-from" style={{ fontSize: 13, color: '#4b5563' }}>From</label>
            <input id="ai-usage-from" type="date" value={from} max={to}
              onChange={e => setFrom(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6 }} />
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <label htmlFor="ai-usage-to" style={{ fontSize: 13, color: '#4b5563' }}>To</label>
            <input id="ai-usage-to" type="date" value={to} min={from}
              onChange={e => setTo(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6 }} />
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {presets.map(p => (
              <button key={p.label} type="button"
                onClick={() => setPreset(p.days)}
                style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 6,
                         background: 'transparent', cursor: 'pointer', fontSize: 13 }}>
                Last {p.label}
              </button>
            ))}
            <button type="button" onClick={load}
              style={{ padding: '6px 14px', border: 'none', borderRadius: 6,
                       background: '#4f46e5', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
              Reload
            </button>
          </div>
        </div>
      </div>

      {err && (
        <div style={{ ...card, color: '#dc2626', borderColor: '#dc2626' }}>{err}</div>
      )}

      {data && !loading && data.totals && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
          <KpiTile label="Total spend"     value={usd(data.totals.total_cost_usd)} big />
          <KpiTile label="Transcription"   value={usd(data.totals.transcription_cost_usd)} />
          <KpiTile label="Analyzer"        value={usd(data.totals.analyzer_cost_usd)} />
          <KpiTile label="Audio minutes"   value={num(data.totals.audio_minutes)} />
        </div>
      )}

      <div style={card}>
        {loading && <div style={{ color: '#6b7280' }}>Loading…</div>}

        {!loading && data && data.tenants.length === 0 && (
          <div style={{ color: '#6b7280' }}>No active tenants.</div>
        )}

        {!loading && data && data.tenants.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Tenant</th>
                <th style={thRight}>Transcriptions</th>
                <th style={thRight}>Audio (min)</th>
                <th style={thRight}>Transcription $</th>
                <th style={thRight}>Analyzer runs</th>
                <th style={thRight}>Prompt tk</th>
                <th style={thRight}>Compl. tk</th>
                <th style={thRight}>Analyzer $</th>
                <th style={thRight}>Total $</th>
              </tr>
            </thead>
            <tbody>
              {data.tenants.map(r => (
                <tr key={r.tenant_id}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{r.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>#{r.tenant_id} · {r.slug}</div>
                  </td>
                  <td style={tdRight}>{num(r.transcription.calls)}</td>
                  <td style={tdRight}>{num(r.transcription.audio_minutes)}</td>
                  <td style={tdRight}>{usd(r.transcription.cost_usd)}</td>
                  <td style={tdRight}>{num(r.analyzer.runs)}</td>
                  <td style={tdRight}>{num(r.analyzer.prompt_tokens)}</td>
                  <td style={tdRight}>{num(r.analyzer.completion_tokens)}</td>
                  <td style={tdRight}>{usd(r.analyzer.cost_usd)}</td>
                  <td style={{ ...tdRight, fontWeight: 600 }}>{usd(r.total_cost_usd)}</td>
                </tr>
              ))}
              <tr style={{ background: '#f9fafb' }}>
                <td style={{ ...td, fontWeight: 700 }}>TOTAL</td>
                <td style={{ ...tdRight, fontWeight: 700 }}>{num(data.totals.transcription_calls)}</td>
                <td style={{ ...tdRight, fontWeight: 700 }}>{num(data.totals.audio_minutes)}</td>
                <td style={{ ...tdRight, fontWeight: 700 }}>{usd(data.totals.transcription_cost_usd)}</td>
                <td style={{ ...tdRight, fontWeight: 700 }}>{num(data.totals.analyzer_runs)}</td>
                <td style={{ ...tdRight, fontWeight: 700 }}>{num(data.totals.prompt_tokens)}</td>
                <td style={{ ...tdRight, fontWeight: 700 }}>{num(data.totals.completion_tokens)}</td>
                <td style={{ ...tdRight, fontWeight: 700 }}>{usd(data.totals.analyzer_cost_usd)}</td>
                <td style={{ ...tdRight, fontWeight: 700, color: '#4f46e5' }}>{usd(data.totals.total_cost_usd)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      <div style={{ ...card, background: '#f9fafb', fontSize: 13, color: '#4b5563' }}>
        <strong>Notes.</strong>{' '}
        Analyzer cost is tracked at the skill level (one row per LLM call) in <code>skill_outputs</code>.
        Transcription cost is per sim-call in <code>sim_calls.transcription_cost_micros</code>.
        Rates live in <code>ModelPricing.php</code> — update when provider pricing changes.
        Zero-usage tenants appear with $0 so the full tenant list is always visible.
      </div>
    </div>
  );
};

const KpiTile: React.FC<{ label: string; value: string; big?: boolean }> = ({ label, value, big }) => (
  <div style={{
    background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
    padding: 16,
  }}>
    <div style={{ fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
      {label}
    </div>
    <div style={{ fontSize: big ? 28 : 22, fontWeight: 700, marginTop: 6, color: big ? '#4f46e5' : '#111827' }}>
      {value}
    </div>
  </div>
);

export default AiUsagePage;
